// People of a role (R-A5): who wears it, what it is limited to and what it does today.
// The table is the compact one of the core — the same as the linked records of a card — so a role page has no table of its own.

import { useMemo, useState } from 'react';
import { avatarUrl, formatDate, recordTitle, rolePeople, type Grant, type GrantState, type Id, type RoleDef } from '@confdeck/core-shared';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Avatar, Button, Chip, SearchField } from '../ui/primitives.tsx';
import { CompactTable } from '../table/CompactTable.tsx';
import s from './team.module.css';

/** What the grant does today, in words of the interface (platform П7). */
const STATE: Record<GrantState, { label: string; color: string }> = {
  live: { label: 'действует', color: 'green' },
  expired: { label: 'истёк', color: 'gray' },
  off: { label: 'приостановлен', color: 'amber' },
};

export function RolePeople({ role, onOpenPerson, onGrant }: {
  role: RoleDef;
  /** The person opens the same way as from the people list. */
  onOpenPerson: (id: string) => void;
  /** Where the role is given: the people tab. */
  onGrant: () => void;
}) {
  const meta = useMeta();
  const client = useSync();
  const [q, setQ] = useState('');
  const rows = useMemo(() => rolePeople(meta.users(), role.id), [meta, role.id]);
  const needle = q.trim().toLowerCase();
  const shown = useMemo(
    () => (needle ? rows.filter((r) => r.user.name.toLowerCase().includes(needle) || r.user.email.toLowerCase().includes(needle)) : rows),
    [rows, needle],
  );
  // The names of the events a grant is limited to: the records of the scope type.
  const scope = client.header?.scope;
  const eventsTick = useTypesTick(scope ? [scope.typeId] : []);
  const names = useMemo(() => {
    const map = new Map<Id, string>();
    if (!scope) return map;
    for (const id of client.store.recordIds(scope.typeId)) {
      const rec = client.store.record(id);
      if (rec) map.set(id, recordTitle(rec, meta));
    }
    return map;
  }, [scope, client.store, meta, eventsTick]);
  const scopeAll = scope ? `Все ${(meta.type(scope.typeId)?.nameMany ?? scope.label).toLowerCase()}` : 'Все';
  const scopeText = (g: Grant) => {
    if (!g.scopeIds) return scopeAll;
    if (!g.scopeIds.length) return '—';
    return g.scopeIds.map((id) => names.get(id) ?? '—').join(', ');
  };
  return (
    <div className={s.peopleBox}>
      <h3 className={s.h3}>Люди роли · {rows.length}</h3>
      {!rows.length ? (
        <div className={s.peopleEmpty}>
          <p className={s.help}>Эта роль пока ни у кого.</p>
          <Button size="sm" variant="secondary" icon="plus" onClick={onGrant}>Выдать роль</Button>
          <p className={s.help}>Роль выдают в разделе «Люди»: откройте человека и добавьте ему роль.</p>
        </div>
      ) : (
        <>
          <div className={s.peopleSearch}>
            <SearchField placeholder="Поиск по имени и email" label="Поиск по людям роли" value={q} onChange={setQ} />
          </div>
          {!shown.length ? (
            <div className={s.peopleEmpty}>
              <p className={s.help}>Никого не нашли — измените поиск.</p>
              <Button size="sm" variant="ghost" onClick={() => setQ('')}>Сбросить</Button>
            </div>
          ) : (
            <>
              <CompactTable className={s.peopleTable} cardsOnPhone label="Люди роли" rows={shown} rowKey={(row, i) => `${row.user.id}-${i}`} onOpen={(row) => onOpenPerson(row.user.id)} columns={[
                { key: 'person', label: 'Человек', render: (row) => (
                  <span className={s.person}>
                    <Avatar name={row.user.name} color={row.user.color} src={avatarUrl(row.user)} size={20} />
                    <span className={s.personBody}>
                      <span className={s.cellName}>{row.user.name}</span>
                      <span className={s.cellSub}>{row.user.email}{row.user.pending ? ' · пароль не задан' : row.user.external ? ' · внештатный' : ''}</span>
                    </span>
                  </span>
                ) },
                { key: 'scope', label: 'Область', render: (row) => scopeText(row.grant) },
                { key: 'until', label: 'Срок', render: (row) => (row.grant.until ? formatDate(row.grant.until) : '—') },
                { key: 'state', label: 'Статус', render: (row) => <Chip color={STATE[row.state].color}>{STATE[row.state].label}</Chip> },
              ]} />
              {shown.length < rows.length && <p className={s.help}>Показаны {shown.length} из {rows.length}</p>}
            </>
          )}
        </>
      )}
    </div>
  );
}
